<template>
  <div
    class='comment-wrapper group/comment relative flex w-full flex-1 rounded-control px-2 py-1 hover:bg-row-alt [&.active]:bg-row-alt'
    :class="{ 'active': edit || deleting, 'grouped': grouped }"
    @click="markAsRead()"
  >
    <div v-if="notRead" class="not-read absolute -top-2.5 right-0 bottom-0 left-2.5 -z-1 rounded-[0.625rem] bg-text opacity-20"></div>
    <div v-if="!grouped" class='mr-2 flex h-full justify-center'>
      <template v-if="comment.integrationClassId">
        <!-- Use skeleton instead of spinner while integrations map loads -->
        <SkeletonLoader class="integration-avatar size-8 rounded-md!" v-if="projectIntegrations === null" type="avatar" />
        <img v-else-if="integrationMeta" :src="integrationMeta.logo" :title="integrationMeta.botName" class="integration-avatar size-8 rounded-md object-contain" />
      </template>
      <TracklifyBotPicture v-else-if="isBot"/>
      <UserAvatar v-else class="cursor-auto!" withDayoffBadge :user='commentator' :fromMcp='comment.fromMcp'/>
    </div>
    <div class='body w-[calc(100%-2.5rem)]' :class="grouped && 'ml-10'" v-if="comment.integrationClassId && projectIntegrations === null">
      <!-- Comment skeleton while integrations are loading -->
      <div :class='HEADER_ROW_CLASS'>
        <SkeletonLoader class="mr-2" type="text" size="small" width="80" />
        <SkeletonLoader type="text" size="small" width="40" />
      </div>
    </div>
    <div
      v-else-if="comment.integrationClassId && comment.template"
      class="body w-[calc(100%-2.5rem)]"
      :class="grouped && 'ml-10'"
    >
      <div v-if="!grouped" :class='HEADER_ROW_CLASS'>
        <strong>{{ displayName }}</strong>
        <Time class='ml-1.5 text-label' :time='comment.createdAt' />
      </div>
      <div class='content-wrapper flex h-auto flex-col'>
        <component
          v-if="integrationTemplateComponent"
          :is="integrationTemplateComponent"
          :context="integrationTemplateContext"
        />
      </div>
    </div>
    <div
      class='body w-[calc(100%-2.5rem)]'
      :class="grouped && 'ml-10'"
      v-else-if="isBot"
    >
      <div v-if="!grouped" :class='HEADER_ROW_CLASS'>
        <strong>Tracklify BOT</strong>
        <Time class='ml-1.5 text-label' :time='comment.createdAt' />
      </div>
      <div class='content-wrapper flex h-auto flex-col'>
        <div class="multilineinput-wrapper">
          <div class="title flex flex-col border border-transparent p-0.5 text-text">
            <p>{{ $t('comments.bot_line1') }}</p>
            <p>{{ $t('comments.bot_line2') }} </p>
            <p>{{ $t('comments.bot_line3') }}</p>
          </div>
        </div>
      </div>
    </div>
    <div class='body w-[calc(100%-2.5rem)]' :class="grouped && 'ml-10'" v-else>
      <div :class='grouped ? GUTTER_CLASS : HEADER_ROW_CLASS'>
        <template v-if="!grouped">
          <strong>{{ displayName }}</strong>
          <Time class='ml-1.5 text-label' :time='comment.createdAt' />
        </template>
        <span v-if="edited" class="text-text-muted" :class="grouped ? 'text-unit' : 'ml-2 text-label'">{{$t('task.edited')}}</span>
      </div>
      <div
        v-if='canPinComment || canEditComment || canDeleteComment'
        :class="[FLOATING_BAR_CLASS, edit || deleting || reactionPickerOpen ? 'flex' : 'hidden group-hover/comment:flex']"
        @click.stop
      >
        <SimpleButton
          v-if="canPinComment"
          :class="COMMENT_ACTION_CLASS"
          variant="ghost"
          :pressed="pinned"
          :disabled="pinning"
          :title="$t('comments.pin_comment')"
          @click="togglePinned"
        >
          <IconPinnedFilled v-if="pinned" class="size-4"/>
          <IconPinned v-else class="size-4"/>
        </SimpleButton>
        <SimpleButton
          v-if="canEditComment"
          :class="COMMENT_ACTION_CLASS"
          variant="ghost"
          :pressed="edit"
          @click="editComment"
        >
          <IconPencil class="size-4"/>
        </SimpleButton>
        <ConfirmButton
          v-if="canDeleteComment"
          :popupTitle="$t('task.Are_you_sure_you_want_to_delete_this_comment')"
          :popupSubTitle="$t('settings.cant_undone')"
          :confirmButtonText="$t('task.Yes')"
          @popup-open="deleting = true;"
          @popup-close="deleting = false;"
          @confirm="deleteComment"
        >
          <template #menu-trigger>
            <SimpleButton :class="[COMMENT_ACTION_CLASS, 'hover:text-red!']" variant="ghost" :pressed="deleting">
              <IconTrash class="size-4"/>
            </SimpleButton>
          </template>
        </ConfirmButton>
        <ReactionAddButton
          v-if="!hasReactions && !comment.readonly && !comment.integrationClassId && comment.userId"
          :comment="comment"
          :taskId="task.id"
          placement="down"
          @open-change="reactionPickerOpen = $event"
          v-slot="{ toggle, active }"
        >
          <SimpleButton
            :class="COMMENT_ACTION_CLASS"
            variant="ghost"
            :pressed="active"
            :title="$t('comments.add_reaction')"
            @click="toggle"
          >
            <IconAddReactionOutlineRounded class="size-4"/>
          </SimpleButton>
        </ReactionAddButton>
      </div>
      <div class='content-wrapper flex h-auto flex-col'>
        <EditorInput
          :id='cid'
          ref='input'
          class='text h-auto w-full justify-center'
          :class="edit ? 'edit my-2.5' : ''"
          @update:opened="onEditorOpened"
          @submit="updateComment"
          @commentAttachImage="$emit('commentAttachImage', $event)"
          :dontSubmitOnEnter="mobileMode"
          :reversedTag="true"
          typeOfEditor="comment"
          :allowSubmit='!task.attachments
            || !task.attachments.find(a => a.commentId === comment.id && !a.uploaded)'
          :key='`cmt-${edit}`'
          :value='commentValue'
          :render='comment.textHTML'
          :allowEdit="edit"
          :tagList="tagList"
          :needAdjustHeight='false'
          :submitOnFocusOut="true"
          :isHideFormat="true"
        />
        <div
          v-if='attachmentImages.length || attachmentFiles.length'
          class="attachments flex flex-col">
          <div
            v-if="attachmentImages.length"
            ref="imagesListRef"
            class="images flex flex-wrap"
            data-image-gallery>
            <div class="image relative mr-2 mb-2 flex min-h-16.25 items-center justify-center rounded-[0.3125rem] border-[0.5px] border-line"
              v-for="attachment in attachmentImages"
              :key="attachment.id"
            >
              <Picture
                class="rounded-[0.3125rem]! [&_img]:rounded-[0.3125rem]!
                       max-sm:max-w-65 max-sm:[&.loading]:max-w-65!
                       max-sm:[&_.img-wrapper]:flex max-sm:[&_.img-wrapper]:max-w-65
                       max-sm:[&_.img-wrapper]:rounded-none! max-sm:[&_.img-wrapper]:bg-[#3b4556]
                       max-sm:[&_img]:max-w-full! max-sm:[&_img]:object-cover!"
                :maxHeight="sizeImage(attachment).maxHeight"
                :maxWidth="sizeImage(attachment).maxWidth"
                :className="sizeImage(attachment).className"
                zoomable 
                border
                :showTools="!readOnly && (comment.userId === user.id)"
                :uploaded="attachment.uploaded"
                :url="attachmentUrl(attachment)"
                :thumbHash="attachment.thumbHash || ''"
                :basePicture="attachment.basePicture || null"
                :color="attachment.dominantColor || ''"
                :imageWidth="attachment.width.toString()"
                :imageHeight="attachment.height.toString()"
                :attachment="attachment"
                :task="task"
                :canSetPreview="canSetTaskPreview"
                :isPreview="attachment.type === 'TASK_PREVIEW'"
                @editImage="editImage(attachment)"
                @deleteImage="deleteAttachment(attachment)"
                @setPreview="setAsPreview(attachment)"
              ></Picture>
            </div>
          </div>
          <div
            v-if="attachmentFiles.length"
            class="files flex flex-wrap"
          >
            <template v-for="attachment in attachmentFiles">
              <!-- TODO: maybe better to store mime and show it for all videos -->
              <PreviewVideo
                :key="attachment.id"
                v-if="['webm', 'mkv', 'mp4', 'mov'].includes((attachment.extension || '').toLowerCase()) && attachment.uploaded"
                :attachment="attachment"
                :videoType="{
                  webm: 'video/webm',
                  mp4: 'video/mp4',
                  mkv: 'video/x-matroska',
                  mov: 'video/quicktime',
                }[(attachment.extension || '').toLowerCase()]"
                :href="baseUrl(attachment)"
              />
              <a
                v-else
                class='file attachment-file relative mr-2'
                :class="!attachment.uploaded && 'attachment-status-info pointer-events-none'"
                @click.prevent="downloadAttachment(attachment)"
              >
                <div class="status-wrapper" :class="attachment.uploaded ? 'hidden' : 'absolute top-2.5 left-0 z-1 block size-7.5'">
                  <Loader
                    v-if="(attachment.send && !attachment.uploaded)"
                    class="absolute! mt-0! size-2.5! bg-text-soft border-l-line! border-y-transparent! border-r-transparent!"
                  />
                  <div
                    class="upload-false relative size-4.5 rounded-full bg-bg2
                           before:absolute before:top-2 before:left-0.5 before:block before:h-0.75 before:w-3.75 before:-rotate-45 before:bg-[rgb(175,6,6)] before:content-['']
                           after:absolute after:top-2 after:left-0.5 after:block after:h-0.75 after:w-3.75 after:rotate-45 after:bg-[rgb(175,6,6)] after:content-['']"
                    v-if="(!attachment.send && !attachment.uploaded)">
                    <span></span>
                  </div>
                </div>
                <PreviewFileGenerator
                  class="mr-2"
                  type="preview"
                  :filename="`${attachment.filename}.${attachment.extension}`"
                  :size="fileSize(attachment)"
                  extendedInfo
                  :extension="attachment.extension.toUpperCase()"
                  :attachmentId="attachment.id"
                />
              </a>
            </template>
          </div>

          <FilePreviewerPopup
            v-if="previewAttachment"
            :filename="`${previewAttachment.filename}.${previewAttachment.extension}`"
            :url="baseUrl(previewAttachment)"
            :user="commentator"
            :when="comment.createdAt"
            @close="previewAttachment = null"
          >
            <PdfViewer
              v-if="previewAttachment.extension.toLowerCase() === 'pdf'"
              :pdfUrl="baseUrl(previewAttachment)"
            />
          </FilePreviewerPopup>
             
        </div>
      </div>
      <CommentReactions
        v-if="!readOnly && !comment.readonly && !comment.integrationClassId && comment.userId"
        :comment="comment"
        :taskId="task.id"
      />
      <slot />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, getCurrentInstance, nextTick, defineAsyncComponent } from 'vue';
import { useStore } from 'vuex';
import humanize from 'humanize';
import axios from 'axios';
import { useSortable } from '@/composables/useSortable';

import Time from '@/components/elements/Time';
import Picture from '@/components/elements/Picture';
import EditorInput from '@/components/elements/EditorInput';
import ConfirmButton from '@/components/elements/ConfirmButton';
import PreviewFileGenerator from '@/components/elements/PreviewFileGenerator';
import PreviewVideo from '@/components/elements/PreviewVideo';
import CommentReactions from '@/components/elements/CommentReactions';
import ReactionAddButton from '@/components/elements/ReactionAddButton';

import TracklifyBotPicture from '@/components/elements/TracklifyBotPicture';
import PdfViewer from '@/components/widgets/PdfViewer.vue';
import FilePreviewerPopup from '@/components/widgets/FilePreviewerPopup.vue';
import { editImageWithPainterro } from '@/store/utils';
import SkeletonLoader from '@/components/elements/SkeletonLoader';

import { IconTrash, IconPencil, IconPinned, IconPinnedFilled } from '@iconify-prerendered/vue-tabler';
import { IconAddReactionOutlineRounded } from '@iconify-prerendered/vue-material-symbols';
import { callAPI } from '@/store/socket';

const { proxy } = getCurrentInstance();

// chip radius: the default control radius turns a 20px icon button (and its hover) into a circle
const COMMENT_ACTION_CLASS = 'p-0.5 rounded-chip!';
const HEADER_ROW_CLASS = 'mb-2 flex items-center text-text';
// grouped comments have no avatar; "edited" sits in its empty column and shows on hover
const GUTTER_CLASS = `absolute top-1 left-0 flex w-12 flex-col items-center whitespace-nowrap
  opacity-0 group-hover/comment:opacity-100 group-[.active]/comment:opacity-100`;
const FLOATING_BAR_CLASS = 'popover-panel absolute -top-2.5 right-5 z-1 items-center gap-0.5 rounded-control px-2 py-1';

const input = ref(null);

const props = defineProps({
  comment: Object,
  // follows a comment of the same author: no avatar or name
  grouped: Boolean,
  project: Object,
  user: Object,
  task: Object,
  pinned: {
    type: Boolean,
    default: false,
  },
  pinnedCount: {
    type: Number,
    default: 0,
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
  disableManagementPreview: {
    type: Boolean,
    default: false,
  },
  notRead: {
    type: Boolean,
    default: false,
  },
  tagList: {
    type: Array,
    default: () => [],
  },
  eventAction: String,
});

const emit = defineEmits(['markAsRead', 'commentAttachImage', 'delete', 'update', 'setpreview', 'pinChanged', 'editStateChanged', 'hiddenChanged']);

const previewAttachment = ref(null);
const store = useStore();

const cid = ref(String);
const deleting = ref(false);
const edit = ref(false);
const reactionPickerOpen = ref(false);
const edited = ref(false);
const imageExtentions = ref(['png', 'jpeg', 'jpg', 'gif', 'bmp', 'webp']);
const pinning = ref(false);
const isCommentEditing = ref(false);
const getSrcImage = (attachment) => {
  console.log('🐕‍🦺 getSrcImage called', attachment);
  return `${attachmentUrl(attachment)}?${attachment && attachment.updatedAt ? attachment.updatedAt : ''}`;
};

const actionAttachImageToTask = (data) => {
  store.dispatch('project/actionAttachImageToTask', data);
};


const editImage = (attachment) => {
  if (document.activeElement) {
    document.activeElement.blur();
  }

  editImageWithPainterro({
    imageUrl: `${getSrcImage(attachment)}?uncache_for_s3_cors=edit`,
    onSave: async (imageData) => {

      // run in background
      actionAttachImageToTask({
        data: imageData.asDataURL(),
        height: imageData.getHeight(),
        width: imageData.getWidth(),
        updTaskId: props.task.id,
        updProjectId: props.task.projectId,
        updStageId: props.task.stageId,
        updWorkspaceId: props.task.workspaceId,
        attachmentId: attachment.id,
        commentId: attachment.commentId,
        nopreview: attachment.type === 'TASK_ATTACHMENT',
      });
    },
  });
  
}

const actionDeleteAttachment = (payload) => store.dispatch('project/actionDeleteAttachment', payload);

const pendingDeleteAttachmentIds = ref(new Set());
// attachment id -> function that actually performs the deferred delete call,
// so we can flush every pending one immediately if the user leaves the task view
const pendingDeleteConfirms = new Map();

function confirmPendingDelete(attachment) {
  if (!pendingDeleteConfirms.has(attachment.id)) return;
  pendingDeleteConfirms.delete(attachment.id);
  actionDeleteAttachment({
    id: attachment.id,
    taskId: props.task.id,
    projectId: props.task.projectId,
    stageId: props.task.stageId,
    type: attachment.type,
    extension: attachment.extension,
  });
  if (hiddenByPendingDelete.value && !pendingDeleteConfirms.size) {
    deleteComment();
  }
}

function confirmAllPendingDeletes() {
  pendingDeleteConfirms.forEach(confirm => confirm());
}

window.addEventListener('beforeunload', confirmAllPendingDeletes);
onBeforeUnmount(() => {
  confirmAllPendingDeletes();
  window.removeEventListener('beforeunload', confirmAllPendingDeletes);
});

const deleteAttachment = (attachment) => {
  pendingDeleteAttachmentIds.value = new Set(pendingDeleteAttachmentIds.value).add(attachment.id);
  pendingDeleteConfirms.set(attachment.id, () => confirmPendingDelete(attachment));

  store.commit('project/mutationPushActionAlert', {
    actionText: proxy.$t('notifications.image_deleted'),
    grayButton: {
      text: proxy.$t('task.manual_CANCEL'),
      method: () => {
        pendingDeleteConfirms.delete(attachment.id);
        const ids = new Set(pendingDeleteAttachmentIds.value);
        ids.delete(attachment.id);
        pendingDeleteAttachmentIds.value = ids;
        store.commit('project/mutationShiftActionAlert');
      },
    },
    confirmButton: {
      text: proxy.$t('system.__ok_button'),
      variant: 'red',
      method: () => {
        store.commit('project/mutationShiftActionAlert');
        confirmPendingDelete(attachment);
      },
    },
  });
};

const mobileMode = computed(() => {
  return store.state.system.mobileMode;
});

const commentValue = computed(() => {
  return props.comment.textDelta ? JSON.parse(props.comment.textDelta) : props.comment.textMD;
});

const hasText = computed(() => {
  const value = commentValue.value;
  if (typeof value === 'string') return !!value.trim();
  return value.ops.some(op => typeof op.insert !== 'string' || op.insert.trim());
});

const hiddenByPendingDelete = computed(() => pendingDeleteAttachmentIds.value.size > 0
  && !hasText.value
  && !props.task.attachments.some(a => a.commentId === props.comment.id && !pendingDeleteAttachmentIds.value.has(a.id)));
watch(hiddenByPendingDelete, state => emit('hiddenChanged', { commentId: props.comment.id, state }));

const commentator = computed(() => {
  return store.getters['user/getUser'](props.comment.userId);
});

const hasReactions = computed(() => {
  const reactions = props.comment.userReactions;
  if (!reactions) return false;
  const parsed = typeof reactions === 'string' ? JSON.parse(reactions) : reactions;
  return Object.keys(parsed).length > 0;
});

const projectIntegrations = computed(() => store.state.project.integrations);
const integrationMeta = computed(() => {
  const id = props.comment.integrationClassId;
  const map = projectIntegrations.value;
  return (id && map && map[id]) ? map[id] : null;
});

const isBot = computed(() => props.comment.textMD === 'TRACKLIFY_BOT' && !props.comment.userId && !props.comment.integrationClassId);

const displayName = computed(() => {
  // Prefer integration botName when integrationClassId is set; fallback gracefully
  if (props.comment.integrationClassId) {
    return integrationMeta.value?.botName || proxy.$t('ui.comment_integration');
  }
  return commentator.value?.username || proxy.$t('ui.comment_unknown_user');
});

const integrationTemplateContext = computed(() => {
  if (!props.comment.templateContext) return {};
  try {
    return JSON.parse(props.comment.templateContext);
  } catch (e) {
    console.error('Failed to parse integration templateContext', e);
    return {};
  }
});

const integrationTemplateComponent = computed(() => {
  if (!props.comment.template) return null;
  const map = {
    github_commit: defineAsyncComponent(() => import('@/integrations/templates/github_commit.vue')),
    github_and_more_commits: defineAsyncComponent(() => import('@/integrations/templates/github_and_more_commits.vue')),
    github_opened_closed_pr: defineAsyncComponent(() => import('@/integrations/templates/github_opened_closed_pr.vue')),
    github_review_pr: defineAsyncComponent(() => import('@/integrations/templates/github_review_pr.vue')),
  };
  return map[props.comment.template] || null;
});

// Workspace admin can delete integration comments
const isWorkspaceAdmin = computed(() => {
  const u = store.getters['user/getUser'](props.user.id);
  return u?.userWorkspace?.role === 'ADMIN';
});

const canSetTaskPreview = computed(() => !props.readOnly && props.user?.id === props.task.creatorId);

const canEditComment = computed(() => !props.readOnly && props.user?.id === props.comment.userId);
const canDeleteComment = computed(() => {
  // forbid deleting task_create_comment same as before
  if (props.eventAction === 'task_create_comment') return false;
  if (props.readOnly) return false;
  if (props.user?.id === props.comment.userId) {
    return true;
  }
  if (isWorkspaceAdmin.value && props.comment.integrationClassId) {
    return true;
  }
  return false;
});

const canPinComment = computed(() => {
  if (props.readOnly) return false;
  return !!(props.task && props.task.id && props.comment && props.comment.id);
});

const attachmentImages = computed(() => {
  return props.task.attachments.filter(a => a.commentId === props.comment.id
    && imageExtentions.value.includes(a.extension.toLowerCase())
    && !pendingDeleteAttachmentIds.value.has(a.id)).sort((a, b) => (((typeof (a.order) === 'number') && (typeof (b.order) === 'number'))
    ? (a.order - b.order)
    : new Date(a.createdAt) - new Date(b.createdAt)));
});

const attachmentFiles = computed(() => {
  return props.task.attachments.filter(a => a.commentId === props.comment.id
    && !imageExtentions.value.includes(a.extension.toLowerCase()));
});

const setAsPreview = (attachment) => {
  const exPreview = props.task.attachments.find(a => a.type === 'TASK_PREVIEW');
  emit('setpreview', {
    id: attachment.id,
    previewId: attachment.id,
    exPreviewId: exPreview ? exPreview.id : null,
    taskId: props.task.id,
    projectId: props.task.projectId,
    stageId: props.task.stageId,
  });
};

const canReorderImages = computed(() => canEditComment.value);

const imagesListRef = ref(null);

function onImagesDragEnd(evt) {
  if (evt.oldIndex === evt.newIndex) return;
  const reordered = [...attachmentImages.value];
  const [moved] = reordered.splice(evt.oldIndex, 1);
  reordered.splice(evt.newIndex, 0, moved);
  store.dispatch('project/actionReorderAttachments', {
    taskId: props.task.id,
    orderedIds: reordered.map(attachment => attachment.id),
  });
}

useSortable(imagesListRef, () => (canReorderImages.value ? {
  animation: 150,
  delay: 250,
  delayOnTouchOnly: true,
  filter: '.image-controll',
  preventOnFilter: false,
  ghostClass: 'opacity-40',
  chosenClass: 'cursor-grabbing',
  onEnd: onImagesDragEnd,
} : null), [() => attachmentImages.value.length, canReorderImages]);

onMounted(() => {
  cid.value = window.utils.genId();
  edited.value = props.comment.createdAt !== props.comment.updatedAt;
});

function markAsRead() {
  emit('markAsRead');
}

function onEditorOpened(state) {
  edit.value = state;
  isCommentEditing.value = state;
  emit('editStateChanged', { commentId: props.comment?.id, state });
}

function sizeImage(attachmentImage, maxWidth = 328, maxHeight = 232) {
  const { width } = attachmentImage;
  const { height } = attachmentImage;
  const className = (width < maxWidth || height < maxHeight) ? '' : 'as-cover';
  // const ratio = Math.min(maxWidth / width, maxHeight / height);
  // width *= ratio;
  // height *= ratio;
  return { maxWidth: `${maxWidth}px`, maxHeight: `${maxHeight}px`, className };
}


async function downloadAttachment(attachment) {  
  const url = baseUrl(attachment);
  const filename = `${attachment.filename}.${attachment.extension}`;
  
  if (['pdf'].includes(attachment.extension.toLowerCase())) {
    previewAttachment.value = attachment;
    return 
  }

  try {
    const response = await axios.get(url, { responseType: 'blob' });
    const blob = new Blob([response.data]);
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error(error);
  }
}

async function editComment() {
  console.log('edit comment', edit.value);
  edit.value = !edit.value;
  await nextTick();
  input.value.activateIfAllowed();
  isCommentEditing.value = edit.value;
  emit('editStateChanged', { commentId: props.comment?.id, state: edit.value });
}

function deleteComment() {
  emit('delete', {
    id: props.comment.id,
    taskId: props.comment.taskId,
    projectId: props.project.id,
    text: props.comment.textMD,
  });
}

function updateComment(e) {
  console.log('update commet', e);
  if (!e) return;

  if (e.delta.ops.length === 1 && e.delta.ops[0].insert.trim.length) {
    return;
  }
  if (props.comment.textHTML === e.html) {
    edit.value = false;
    isCommentEditing.value = false;
    emit('editStateChanged', { commentId: props.comment?.id, state: false });
    return;
  }
  emit('update', {
    id: props.comment.id,
    text: e,
    taskId: props.comment.taskId,
    projectId: props.project.id,
    userId: props.comment.userId,
    eventAction: props.eventAction,
  });
  edited.value = true;
  edit.value = false;
  isCommentEditing.value = false;
  emit('editStateChanged', { commentId: props.comment?.id, state: false });
}

async function togglePinned() {
  if (!canPinComment.value || pinning.value) return;

  if (!props.pinned && props.pinnedCount >= 5) {
    store.commit('system/showAlert', {
      message: proxy.$t ? proxy.$t('comments.max_pins_reached') : 'Maximum 5 pinned comments allowed',
      type: 'warning',
      timeout: 5,
    });
    return;
  }

  pinning.value = true;
  try {
    const method = props.pinned ? 'unpinTaskComment' : 'pinTaskComment';
    const res = await callAPI({
      method,
      params: { taskId: props.task.id, commentId: props.comment.id },
      async: true,
    });
    emit('pinChanged', { commentId: props.comment.id, pinned: !props.pinned });
  } catch (e) {
    console.error('Failed to toggle pinned state', {
      taskId: props.task?.id,
      commentId: props.comment?.id,
      pinned: props.pinned,
      error: e,
    });
  } finally {
    pinning.value = false;
  }
}

const attachmentUrl = (attachment) => {
  return `${proxy.s3base}/attachments/${attachment.workspaceId}/${attachment.projectId}/`
    + `${attachment.taskId}/${attachment.id}.${attachment.extension}`;
};

const fileSize = (attachment) => {
  return humanize.filesize(attachment.size, 1024, 0)
    .replace('bytes', 'B')
    .replace(' ', '');
};

const baseUrl = (attachment) => {
  if (!attachment.uploaded) {
    return '';
  }
  return `${proxy.s3base}/attachments/${attachment.workspaceId}/${attachment.projectId}/`
    + `${attachment.taskId}/${attachment.id}.${attachment.extension}`;
};
</script>

<style lang='scss' scoped>

.comment-wrapper {
  .body {
    .content-wrapper {
      .text {
        :deep(.title) {
          border: none;
          padding: 0;
          margin: 0;
          p {
            text-align: left;
            min-height: 8px;
            line-height: 24px;
            word-break: normal;
          }
          blockquote {
            display: block;
            margin-inline-start: 0px;
            padding-left: 10px;
            border-left: 3px solid var(--text-soft);
            margin-inline-end: 30px;
          }
        }
        :deep(pre) {
          overflow-x: auto;
          &::-webkit-scrollbar {
            width: 8px;
            border-radius: 3px;
          }
          &::-webkit-scrollbar-track {
            border-radius: 6px;
          }
          &::-webkit-scrollbar-thumb {
            background: rgba(black, 0.65);
            &:hover {
              background: rgba(black, 0.75);
            }
          }
        }
      }
    }
  }
}
</style>
